const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

interface UploadOptions {
  file: File
  type: 'image' | 'video'
  onProgress?: (percent: number) => void
}

export function uploadFile({ file, type, onProgress }: UploadOptions): Promise<{ url: string; filename: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    formData.append(type, file)

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText))
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Upload failed')))

    const token = localStorage.getItem('admin_token')
    xhr.open('POST', `${API_BASE}/upload/${type}`)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(formData)
  })
}

// Chunked video upload for large files (up to 5GB)
const CHUNK_SIZE = 10 * 1024 * 1024 // 10MB per chunk

interface ChunkedUploadOptions {
  file: File
  onProgress?: (percent: number) => void
  signal?: AbortSignal
  videoId?: string
}

export async function uploadVideoChunked({ file, onProgress, signal, videoId }: ChunkedUploadOptions): Promise<{ url: string; filename: string }> {
  const token = localStorage.getItem('admin_token')
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)

  // Upload each chunk
  for (let i = 0; i < totalChunks; i++) {
    if (signal?.aborted) throw new Error('Upload cancelled')

    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunk = file.slice(start, end)

    const formData = new FormData()
    formData.append('chunk', chunk, file.name)
    formData.append('uploadId', uploadId)
    formData.append('chunkIndex', String(i))
    formData.append('totalChunks', String(totalChunks))

    const res = await fetch(`${API_BASE}/upload/video/chunk`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal,
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.message || `Chunk ${i} upload failed`)
    }

    if (onProgress) {
      onProgress(Math.round(((i + 1) / totalChunks) * 95)) // 0-95% for chunks
    }
  }

  // Merge chunks
  if (onProgress) onProgress(96)

  const mergeBody: Record<string, unknown> = { uploadId, totalChunks, filename: file.name }
  if (videoId) mergeBody.videoId = videoId

  const mergeRes = await fetch(`${API_BASE}/upload/video/merge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(mergeBody),
    signal,
  })

  if (!mergeRes.ok) {
    const body = await mergeRes.json().catch(() => ({}))
    throw new Error(body.message || 'Merge failed')
  }

  if (onProgress) onProgress(100)

  return mergeRes.json()
}
